import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Linkedin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "rounded-none border-0 border-b border-pine/25 bg-transparent px-0 py-3 font-sans text-base shadow-none focus-visible:ring-0 focus-visible:border-forest placeholder:text-pine/35";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry received. Zecharias will reach out shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email Info@bookcarellc.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
      <div className="grid grid-cols-1 gap-20 md:grid-cols-12">
        <div className="md:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terra"
          >
            Schedule a Consultation
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="mb-10 font-serif text-4xl font-medium leading-tight tracking-tight text-pine md:text-5xl"
            data-testid="contact-heading"
          >
            Tell us where the <span className="italic text-forest">numbers</span> hurt.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-12 max-w-md text-base leading-relaxed text-pine/70"
          >
            Every engagement begins with a free consultation. Share a few details and we'll respond
            within one business day — or reach out directly.
          </motion.p>
          <div className="flex flex-col gap-6" data-testid="contact-details">
            <a href="mailto:Info@bookcarellc.com" data-testid="contact-email-link" className="group flex items-center gap-4">
              <Mail size={18} className="text-terra" />
              <span className="font-mono text-sm text-pine/80 transition-colors duration-300 group-hover:text-forest">Info@bookcarellc.com</span>
            </a>
            <a href="tel:+14253459757" data-testid="contact-phone-link" className="group flex items-center gap-4">
              <Phone size={18} className="text-terra" />
              <span className="font-mono text-sm text-pine/80 transition-colors duration-300 group-hover:text-forest">425-345-9757</span>
            </a>
            <div className="flex items-center gap-4">
              <MapPin size={18} className="text-terra" />
              <span className="font-mono text-sm text-pine/80" data-testid="contact-location">Farmersville, TX · Serving clients in person anywhere in the USA and remotely</span>
            </div>
            <a
              href="https://linkedin.com/in/zecharias-mesgane"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="group flex items-center gap-4"
            >
              <Linkedin size={18} className="text-terra" />
              <span className="font-mono text-sm text-pine/80 transition-colors duration-300 group-hover:text-forest">linkedin.com/in/zecharias-mesgane</span>
            </a>
          </div>
        </div>
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="border border-linen bg-white p-10 md:col-span-7 md:p-14"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            <div>
              <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Full Name *</Label>
              <Input id="name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name-input" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Email *</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email-input" className={inputCls} />
            </div>
            <div>
              <Label htmlFor="phone" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Phone</Label>
              <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="(Optional)" data-testid="contact-phone-input" className={inputCls} />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">Service of Interest</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="contact-service-select" className="mt-2 rounded-none border-0 border-b border-pine/25 bg-transparent px-0 shadow-none focus:ring-0">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bookkeeping & Accounting" data-testid="service-option-bookkeeping">Bookkeeping &amp; Accounting</SelectItem>
                  <SelectItem value="Tax Preparation & Planning" data-testid="service-option-tax">Tax Preparation &amp; Planning</SelectItem>
                  <SelectItem value="Fractional CFO & Controller" data-testid="service-option-cfo">Fractional CFO &amp; Controller</SelectItem>
                  <SelectItem value="Career Coaching" data-testid="service-option-coaching">Career Coaching</SelectItem>
                  <SelectItem value="Financial Solution Strategies" data-testid="service-option-strategies">Financial Solution Strategies</SelectItem>
                  <SelectItem value="Other" data-testid="service-option-other">Something else</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-10">
            <Label htmlFor="message" className="font-mono text-[10px] uppercase tracking-[0.25em] text-pine/60">How can we help? *</Label>
            <Textarea
              id="message"
              required
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Tell us about your books, your taxes, or your career goals..."
              data-testid="contact-message-input"
              className={`${inputCls} resize-none`}
            />
          </div>
          <motion.button
            type="submit"
            disabled={sending}
            whileTap={{ scale: 0.97 }}
            data-testid="contact-form-submit-button"
            className="mt-12 w-full border border-forest bg-forest px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-transparent hover:text-forest disabled:opacity-50 md:w-auto"
          >
            {sending ? "Sending..." : "Request Free Consultation"}
          </motion.button>
        </motion.form>
      </div>
    </section>
  );
}
